import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { useCarriers } from '../lib/useCarriers.js';
import { Button, Card, inputClass } from './ui.js';

interface Alias {
  id: string;
  carrierId: string;
  carrierName: string | null;
  alias: string;
  address: string;
  allowedSenderDomain: string | null;
  filenamePeriodRegex: string | null;
  enabled: boolean;
  lastReceivedAt: number | null;
  lastStatus: string | null;
}

/**
 * Email statement ingest (app-only, gated by the `emailIngest` entitlement). Each
 * carrier gets a unique inbound address; forward statements there (or have the carrier
 * email them) and they ingest automatically — the address alone routes them. Mirrors
 * the SFTP card. Direct fetch, off the SDK.
 */
export function EmailIngestSettings() {
  const { baseUrl, token, entitlements } = useAuth();
  const { carriers } = useCarriers();
  const url = `${baseUrl.replace(/\/$/, '')}/inbound-aliases`;
  const emailEnabled = entitlements.emailIngest;
  const auth = () => ({ authorization: `Bearer ${token}`, 'content-type': 'application/json' });

  const [aliases, setAliases] = useState<Alias[] | null>(null);
  const [domain, setDomain] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [senderDomain, setSenderDomain] = useState('');
  const [periodRegex, setPeriodRegex] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch(url, { headers: { authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!b) return;
        setAliases(b.aliases);
        setDomain(b.domain);
      })
      .catch(() => {});
  }, [url, token]);

  useEffect(() => {
    if (token && emailEnabled) load();
  }, [token, emailEnabled, load]);

  const create = async () => {
    if (!carrierId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: auth(),
        body: JSON.stringify({
          carrierId,
          allowedSenderDomain: senderDomain.trim() || undefined,
          filenamePeriodRegex: periodRegex.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Failed (${res.status})`);
      }
      setCarrierId('');
      setSenderDomain('');
      setPeriodRegex('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await fetch(`${url}/${id}`, { method: 'DELETE', headers: auth() });
    load();
  };
  const toggle = async (a: Alias) => {
    await fetch(`${url}/${a.id}`, {
      method: 'PATCH',
      headers: auth(),
      body: JSON.stringify({ enabled: !a.enabled }),
    });
    load();
  };
  const copy = (addr: string) => {
    navigator.clipboard?.writeText(addr).then(() => {
      setCopied(addr);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  if (!entitlements.emailIngest) return null;

  return (
    <Card>
      <h2 className="font-semibold">Email ingestion</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Give each carrier a unique inbound address. Forward their statement emails there (or have
        the carrier send them), and we ingest the attachment automatically — the address routes it
        to the right carrier. Only <code>.csv / .xlsx / .xls</code> attachments are accepted.
      </p>

      {aliases && aliases.length > 0 && (
        <div className="mt-4 space-y-2">
          {aliases.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-200 p-3 dark:border-neutral-800"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">
                  {a.carrierName ?? a.carrierId.slice(0, 8)}
                </div>
                <button
                  type="button"
                  onClick={() => copy(a.address)}
                  className="mt-0.5 truncate font-mono text-xs text-emerald-700 hover:underline dark:text-emerald-400"
                  title="Copy address"
                >
                  {a.address} {copied === a.address ? '· copied ✓' : '· copy'}
                </button>
                <div className="mt-0.5 text-[11px] text-neutral-400">
                  {a.allowedSenderDomain ? `only from @${a.allowedSenderDomain} · ` : ''}
                  {a.lastReceivedAt
                    ? `last: ${a.lastStatus} · ${new Date(a.lastReceivedAt).toLocaleDateString()}`
                    : 'no emails yet'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggle(a)}
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${a.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}
              >
                {a.enabled ? 'Active' : 'Paused'}
              </button>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="text-xs font-medium text-rose-600 hover:underline dark:text-rose-400"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid items-end gap-2 sm:grid-cols-[1.2fr_1.2fr_1.4fr_auto]">
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Carrier</span>
          <select
            className={inputClass}
            value={carrierId}
            onChange={(e) => setCarrierId(e.target.value)}
          >
            <option value="">Select…</option>
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Only accept from (optional)</span>
          <input
            className={inputClass}
            value={senderDomain}
            placeholder="carrier.com"
            onChange={(e) => setSenderDomain(e.target.value)}
          />
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Filename period rule (optional)</span>
          <input
            className={inputClass}
            value={periodRegex}
            placeholder="(?<year>\d{4})-(?<month>\d{2})"
            onChange={(e) => setPeriodRegex(e.target.value)}
          />
        </label>
        <Button onClick={create} loading={busy} disabled={!carrierId}>
          Add address
        </Button>
      </div>
      <p className="mt-1.5 text-[11px] text-neutral-400">
        The period comes from the filename rule when set (named <code>year</code>/<code>month</code>{' '}
        groups). Without it, an email whose period we can’t determine is held for review rather than
        guessed. Domain:{' '}
        <span className="font-mono">{domain || 'inbound.commissionsight.com'}</span>
      </p>
      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
    </Card>
  );
}
